<div class="license-plate-page p-3 sm:p-5 max-w-7xl mx-auto space-y-4">

  <!-- ── Top Header ─────────────────────────────────────────────────────────── -->
  <div
    class="flex flex-col sm:flex-row sm:items-center justify-between gap-3 bg-white p-4 sm:p-5 rounded-xl shadow-xs border border-slate-200">
    <div class="space-y-0.5">
      <div class="flex items-center gap-2.5">
        <div class="p-2 bg-blue-50 text-blue-600 rounded-lg flex items-center justify-center">
          <mat-icon class="text-xl">directions_car</mat-icon>
        </div>
        <h1 class="text-lg sm:text-xl font-bold text-slate-900 tracking-tight">License Plates</h1>
      </div>
      <p class="text-xs text-slate-500 max-w-2xl">
        360° management of license plates across Parkers, Parking Rights, Stays, and Organizations with strict space
        authorization scoping.
      </p>
    </div>

    <!-- Scope Badge -->
    <div
      class="flex items-center gap-2 self-start sm:self-auto bg-slate-50 border border-slate-200 rounded-lg px-3 py-1.5 shrink-0">
      <mat-icon class="text-slate-400 text-base">admin_panel_settings</mat-icon>
      <div class="text-xs">
        <span class="text-slate-500">Access Scope: </span>
        <span class="font-semibold text-slate-700">
          @if (isAdministrator()) {
          Global Admin (All Spaces)
          } @else {
          {{ accessibleSpaces().length }} Accessible Space(s)
          }
        </span>
      </div>
    </div>
  </div>

  <!-- ── Search & Discovery Bar ─────────────────────────────────────────────── -->
  <jjames-card class="overflow-hidden">
    <div class="p-3.5 sm:p-4">
      <div class="flex flex-col sm:flex-row items-stretch sm:items-center gap-2.5">
        <div class="relative flex-1">
          <mat-icon class="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-lg">search</mat-icon>
          <input type="text"
            class="w-full pl-9 pr-8 py-2 bg-slate-50 border border-slate-200 rounded-lg text-xs sm:text-sm font-medium text-slate-800 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all uppercase tracking-wider"
            placeholder="Search by license plate (e.g. W1234AB, MXY123)..." [ngModel]="plateQuery()"
            (ngModelChange)="plateQuery.set($event)" (keyup.enter)="searchPlates()" />
          @if (plateQuery()) {
          <button (click)="clearSearch()"
            class="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 transition-colors"
            title="Clear search">
            <mat-icon class="text-base">close</mat-icon>
          </button>
          }
        </div>

        <button mat-flat-button color="primary" (click)="searchPlates()"
          [disabled]="isSearchingPlates() || !plateQuery().trim()"
          class="h-9 px-4 font-medium rounded-lg shadow-xs text-xs shrink-0">
          @if (isSearchingPlates()) {
          <div class="flex items-center gap-1.5">
            <mat-spinner diameter="15" class="border-white"></mat-spinner>
            <span>Searching…</span>
          </div>
          } @else {
          <div class="flex items-center gap-1.5">
            <mat-icon class="text-base">search</mat-icon>
            <span>Search Plate</span>
          </div>
          }
        </button>
      </div>

      <!-- Search Results Dropdown / List (Only visible when no plate is currently inspected) -->
      @if (!selectedPlate() && plateSearched() && !isSearchingPlates()) {
      @if (plateResults().length === 0) {
      <div class="mt-3 p-3 rounded-lg bg-slate-50 border border-slate-200 text-center text-xs text-slate-500">
        <mat-icon class="text-2xl text-slate-300 block mb-1">search_off</mat-icon>
        No license plates found matching "<strong class="text-slate-700">{{ plateQuery() }}</strong>".
      </div>
      } @else if (plateResults().length > 1) {
      <div class="mt-3.5 space-y-2.5">
        <div class="flex items-center justify-between">
          <span class="text-[11px] font-bold text-slate-500 uppercase tracking-wider">
            Found {{ plateResults().length }} matches — select one to inspect:
          </span>
          <button (click)="clearSearch()" class="text-[11px] text-slate-400 hover:text-slate-600 transition-colors">
            Clear Results
          </button>
        </div>
        <div class="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2.5">
          @for (plate of plateResults(); track plate.id) {
          <div (click)="selectPlate(plate)"
            class="p-2.5 bg-white hover:bg-blue-50/70 hover:border-blue-300 border border-slate-200 rounded-lg cursor-pointer transition-all flex items-center justify-between group shadow-2xs hover:shadow-xs">
            <div class="flex items-center gap-2.5">
              <app-plate [number]="plate.fullNumber || ''" [countryCode]="plate.countryCode ?? 'AT'"></app-plate>
              <div>
                <div class="text-xs font-semibold text-slate-700">{{ getPlateType(plate) }}</div>
                <div class="text-[10px] text-slate-400">{{ plate.createdAt | date:'dd.MM.yyyy HH:mm' }}</div>
              </div>
            </div>
            <mat-icon
              class="text-slate-300 group-hover:text-blue-600 group-hover:translate-x-0.5 transition-all text-base">arrow_forward</mat-icon>
          </div>
          }
        </div>
      </div>
      }
      }
    </div>
  </jjames-card>

  <!-- ── Browse Recent Plates (when no plate selected) ──────────────────────── -->
  @if (!selectedPlate() && !isSearchingPlates() && (!plateSearched() || plateResults().length === 0)) {
  <jjames-card>
    <div class="p-4 sm:p-5 space-y-3">
      <div class="flex items-center justify-between border-b border-slate-100 pb-3">
        <div>
          <h2 class="text-sm font-bold text-slate-900">Browse Registered Plates</h2>
          <p class="text-xs text-slate-500">Quickly select a license plate from the database to view complete 360°
            details.</p>
        </div>
        <button mat-stroked-button (click)="loadBrowsePlates()" [disabled]="isBrowseLoading()"
          class="text-xs font-medium h-8">
          <mat-icon class="text-sm mr-1">refresh</mat-icon> Refresh
        </button>
      </div>

      @if (isBrowseLoading()) {
      <div class="flex flex-col items-center justify-center py-12 text-slate-400 space-y-2">
        <mat-spinner diameter="30"></mat-spinner>
        <span class="text-xs">Loading license plates database…</span>
      </div>
      } @else if (browsePlates().length === 0) {
      <div class="py-10 text-center text-slate-400 text-xs">
        <mat-icon class="text-3xl text-slate-300 mb-1">directions_car</mat-icon>
        <p>No license plates found in the system.</p>
      </div>
      } @else {
      <div class="overflow-x-auto">
        <table class="w-full text-left text-xs">
          <thead class="bg-slate-50 text-slate-500 uppercase tracking-wider border-y border-slate-200">
            <tr>
              <th class="py-2.5 px-3 font-semibold">License Plate</th>
              <th class="py-2.5 px-3 font-semibold">Country</th>
              <th class="py-2.5 px-3 font-semibold">Type</th>
              <th class="py-2.5 px-3 font-semibold">Created Date</th>
              <th class="py-2.5 px-3 font-semibold text-right">Actions</th>
            </tr>
          </thead>
          <tbody class="divide-y divide-slate-100">
            @for (plate of browsePlates(); track plate.id) {
            <tr class="hover:bg-slate-50/80 transition-colors">
              <td class="py-2 px-3">
                <app-plate [number]="plate.fullNumber || ''" [countryCode]="plate.countryCode ?? 'AT'"></app-plate>
              </td>
              <td class="py-2 px-3">
                <span
                  class="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold bg-slate-100 text-slate-700 border border-slate-200">
                  {{ plate.countryCode || 'AT' }}
                </span>
              </td>
              <td class="py-2 px-3">
                <span class="text-xs text-slate-600 font-medium">{{ getPlateType(plate) }}</span>
              </td>
              <td class="py-2 px-3 text-xs text-slate-500">
                {{ plate.createdAt ? (plate.createdAt | date:'dd.MM.yyyy HH:mm') : '—' }}
              </td>
              <td class="py-2 px-3 text-right">
                <button mat-flat-button color="primary" (click)="selectPlate(plate)"
                  class="text-xs font-medium h-7 px-2.5">
                  Inspect
                </button>
              </td>
            </tr>
            }
          </tbody>
        </table>
      </div>

      <!-- Pagination -->
      <div class="pt-2.5 border-t border-slate-100 flex justify-end">
        <jjames-paginator [config]="browsePagination" (pageChange)="onBrowsePageChange($event)"></jjames-paginator>
      </div>
      }
    </div>
  </jjames-card>
  }

  <!-- ── 360° Selected Plate Management View ────────────────────────────────── -->
  @if (selectedPlate(); as plate) {

  <!-- Search Results Return Banner (if arrived from multiple search matches) -->
  @if (plateResults().length > 1) {
  <div
    class="flex flex-wrap items-center justify-between gap-2 bg-blue-50 border border-blue-200/80 rounded-xl px-3.5 py-2 text-xs text-blue-900 shadow-2xs">
    <div class="flex items-center gap-2">
      <mat-icon class="text-blue-600 text-sm">search</mat-icon>
      <span>Viewing details for <strong>{{ plate.fullNumber }}</strong> (from {{ plateResults().length }} matches for
        "{{ plateQuery() }}")</span>
    </div>
    <button (click)="clearSelectedPlate()"
      class="font-semibold text-blue-700 hover:text-blue-900 hover:underline flex items-center gap-1 cursor-pointer">
      <mat-icon class="text-sm">arrow_back</mat-icon> Back to all {{ plateResults().length }} matches
    </button>
  </div>
  }

  <!-- Hero Card -->
  <div class="bg-white rounded-xl shadow-xs border border-slate-200 p-4 sm:p-5 space-y-4">
    <div class="flex flex-col md:flex-row md:items-center justify-between gap-4 pb-4 border-b border-slate-100">

      <!-- Left: Plate Graphic & Details (No scale transform overlap) -->
      <div class="flex flex-wrap items-center gap-3 sm:gap-4">
        <div class="shrink-0 flex items-center">
          <app-plate [number]="plate.fullNumber || ''" [countryCode]="plate.countryCode ?? 'AT'"></app-plate>
        </div>

        <div class="space-y-1">
          <div class="flex flex-wrap items-center gap-2">
            <span class="font-mono text-base sm:text-lg font-bold text-slate-900 tracking-wider">{{ plate.fullNumber
              }}</span>
            <button (click)="copyPlateNumber()"
              class="p-1 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-md transition-colors"
              [matTooltip]="copiedPlate() ? 'Copied!' : 'Copy plate number'">
              <mat-icon class="text-sm">{{ copiedPlate() ? 'check' : 'content_copy' }}</mat-icon>
            </button>

            <!-- Type & Country Badge -->
            <span
              class="text-[11px] font-semibold px-2 py-0.5 rounded-full bg-slate-100 text-slate-700 border border-slate-200">
              {{ getPlateType(plate) }}
            </span>
            <span
              class="text-[11px] font-semibold px-1.5 py-0.5 rounded-md bg-blue-50 text-blue-700 border border-blue-200">
              {{ plate.countryCode || 'AT' }}
            </span>
          </div>

          <div class="flex flex-wrap items-center gap-2 text-xs text-slate-500">
            <span>Registered: {{ plate.createdAt ? (plate.createdAt | date:'dd.MM.yyyy HH:mm') : '—' }}</span>
          </div>

          <!-- Site Classification (in force now, one chip per site) -->
          @if (currentClassifications().length) {
          <div class="flex flex-wrap items-center gap-1.5">
            @for (c of currentClassifications(); track c.id) {
            <span
              class="text-[11px] font-semibold px-2 py-0.5 rounded-full bg-violet-50 text-violet-700 border border-violet-200"
              [matTooltip]="'Site classification · ' + c.source">
              {{ getSpaceName(c.parkingSpaceId) }} · {{ classificationLabelKey(c.classification) | translate }}
            </span>
            }
          </div>
          }
        </div>
      </div>

      <!-- Right: Actions -->
      <div class="flex flex-wrap items-center gap-1.5 self-start md:self-auto shrink-0">
        <button mat-stroked-button (click)="openEditModal()"
          class="h-8 text-xs font-medium text-slate-700 border-slate-200 px-3">
          <mat-icon class="text-sm mr-1">edit</mat-icon> Edit Plate
        </button>
        <button mat-stroked-button (click)="refreshCurrentPlate()" [disabled]="loadingEnriched()"
          class="h-8 text-xs font-medium text-slate-700 border-slate-200 px-3">
          <mat-icon class="text-sm mr-1" [class.animate-spin]="loadingEnriched()">refresh</mat-icon> Refresh
        </button>
        <button mat-stroked-button (click)="clearSelectedPlate()"
          class="h-8 text-xs font-medium text-slate-600 border-slate-200 hover:bg-slate-50 px-3">
          <mat-icon class="text-sm mr-1">arrow_back</mat-icon>
          {{ plateResults().length > 0 ? 'Back to Results (' + plateResults().length + ')' : 'Back to List' }}
        </button>
      </div>
    </div>

    <!-- Active Stay Banner (if vehicle is currently in a parking space) -->
    @if (ongoingStay(); as stay) {
    <div
      class="bg-gradient-to-r from-emerald-600 to-teal-700 text-white rounded-lg p-3 px-4 shadow-xs flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2.5">
      <div class="flex items-center gap-2.5">
        <div class="p-1.5 bg-white/20 rounded-md backdrop-blur-xs">
          <mat-icon class="text-lg">local_parking</mat-icon>
        </div>
        <div>
          <div class="flex items-center gap-1.5">
            <span class="font-bold text-xs">CURRENTLY PARKED</span>
            <span class="inline-block w-2 h-2 rounded-full bg-emerald-300 animate-ping"></span>
          </div>
          <div class="text-xs text-emerald-100">
            At <strong>{{ getSpaceName(stay.parkingSpaceId) }}</strong> (Zone: {{ stay.parkingZoneDetails?.name || 'All
            Zones' }}) • Since {{ stay.startedAt | date:'short' }}
          </div>
        </div>
      </div>
      <div class="text-[11px] bg-black/25 px-2.5 py-1 rounded-md font-medium self-end sm:self-auto">
        Session Active
      </div>
    </div>
    }

    <!-- ── 4 KPI Summary Cards ────────────────────────────────────────────── -->
    <div class="grid grid-cols-2 lg:grid-cols-5 gap-2.5 sm:gap-3">

      <!-- Parkers KPI -->
      <div (click)="activeTab.set('parkers')" class="p-3 rounded-lg border transition-all cursor-pointer"
        [class.bg-blue-50]="activeTab() === 'parkers'" [class.border-blue-300]="activeTab() === 'parkers'"
        [class.bg-slate-50]="activeTab() !== 'parkers'" [class.border-slate-200]="activeTab() !== 'parkers'">
        <div class="flex items-center justify-between text-slate-500 mb-0.5">
          <span class="text-[11px] font-semibold uppercase tracking-wider">Parkers</span>
          <mat-icon class="text-base text-blue-600">person</mat-icon>
        </div>
        <div class="text-lg sm:text-xl font-bold text-slate-900">{{ holders().length }}</div>
        <div class="text-[11px] mt-0.5 font-medium truncate" [class.text-emerald-600]="isSettled()"
          [class.text-amber-600]="!isSettled()">
          {{ isSettled() ? '✓ 1 Settled Owner' : (registeredHolders().length > 1 ? '⚠️ Needs Resolution' : 'Guest
          records') }}
        </div>
      </div>

      <!-- Rights KPI -->
      <div (click)="activeTab.set('rights')" class="p-3 rounded-lg border transition-all cursor-pointer"
        [class.bg-blue-50]="activeTab() === 'rights'" [class.border-blue-300]="activeTab() === 'rights'"
        [class.bg-slate-50]="activeTab() !== 'rights'" [class.border-slate-200]="activeTab() !== 'rights'">
        <div class="flex items-center justify-between text-slate-500 mb-0.5">
          <span class="text-[11px] font-semibold uppercase tracking-wider">Parking Rights</span>
          <mat-icon class="text-base text-indigo-600">verified_user</mat-icon>
        </div>
        <div class="text-lg sm:text-xl font-bold text-slate-900">{{ parkingRights().length }}</div>
        <div class="text-[11px] text-slate-500 mt-0.5 truncate">In authorized spaces</div>
      </div>

      <!-- Transactions KPI -->
      <div (click)="activeTab.set('transactions')" class="p-3 rounded-lg border transition-all cursor-pointer"
        [class.bg-blue-50]="activeTab() === 'transactions'" [class.border-blue-300]="activeTab() === 'transactions'"
        [class.bg-slate-50]="activeTab() !== 'transactions'" [class.border-slate-200]="activeTab() !== 'transactions'">
        <div class="flex items-center justify-between text-slate-500 mb-0.5">
          <span class="text-[11px] font-semibold uppercase tracking-wider">Stays & Sessions</span>
          <mat-icon class="text-base text-teal-600">receipt_long</mat-icon>
        </div>
        <div class="text-lg sm:text-xl font-bold text-slate-900">{{ transactions().length }}</div>
        <div class="text-[11px] text-slate-500 mt-0.5 truncate">
          {{ ongoingStay() ? '1 Active Stay' : 'Completed stays' }}
        </div>
      </div>

      <!-- Organizations KPI -->
      <div (click)="activeTab.set('organizations')" class="p-3 rounded-lg border transition-all cursor-pointer"
        [class.bg-blue-50]="activeTab() === 'organizations'" [class.border-blue-300]="activeTab() === 'organizations'"
        [class.bg-slate-50]="activeTab() !== 'organizations'"
        [class.border-slate-200]="activeTab() !== 'organizations'">
        <div class="flex items-center justify-between text-slate-500 mb-0.5">
          <span class="text-[11px] font-semibold uppercase tracking-wider">Organizations</span>
          <mat-icon class="text-base text-purple-600">corporate_fare</mat-icon>
        </div>
        <div class="text-lg sm:text-xl font-bold text-slate-900">{{ organizations().length }}</div>
        <div class="text-[11px] text-slate-500 mt-0.5 truncate">Pools & Accounts</div>
      </div>

      <!-- Credit KPI -->
      <div (click)="activeTab.set('balance')" class="p-3 rounded-lg border transition-all cursor-pointer"
        [class.bg-blue-50]="activeTab() === 'balance'" [class.border-blue-300]="activeTab() === 'balance'"
        [class.bg-slate-50]="activeTab() !== 'balance'" [class.border-slate-200]="activeTab() !== 'balance'">
        <div class="flex items-center justify-between text-slate-500 mb-0.5">
          <span class="text-[11px] font-semibold uppercase tracking-wider">Credit</span>
          <mat-icon class="text-base text-emerald-600">account_balance_wallet</mat-icon>
        </div>
        <div class="text-lg sm:text-xl font-bold text-slate-900">{{ totalCreditLabel() }}</div>
        <div class="text-[11px] text-slate-500 mt-0.5 truncate">Coin overpayments</div>
      </div>
    </div>

    <!-- ── Tab Navigation Header ─────────────────────────────────────────── -->
    <div class="border-b border-slate-200">
      <nav class="flex space-x-4 sm:space-x-6 overflow-x-auto no-scrollbar" aria-label="Tabs">
        <button (click)="activeTab.set('parkers')"
          class="py-2.5 px-1 border-b-2 font-semibold text-xs sm:text-sm whitespace-nowrap transition-all flex items-center gap-1.5"
          [class.border-blue-600]="activeTab() === 'parkers'" [class.text-blue-600]="activeTab() === 'parkers'"
          [class.border-transparent]="activeTab() !== 'parkers'" [class.text-slate-500]="activeTab() !== 'parkers'"
          [class.hover:text-slate-700]="activeTab() !== 'parkers'">
          <mat-icon class="text-base">person</mat-icon>
          <span>Parkers & Ownership ({{ holders().length }})</span>
        </button>

        <button (click)="activeTab.set('rights')"
          class="py-2.5 px-1 border-b-2 font-semibold text-xs sm:text-sm whitespace-nowrap transition-all flex items-center gap-1.5"
          [class.border-blue-600]="activeTab() === 'rights'" [class.text-blue-600]="activeTab() === 'rights'"
          [class.border-transparent]="activeTab() !== 'rights'" [class.text-slate-500]="activeTab() !== 'rights'"
          [class.hover:text-slate-700]="activeTab() !== 'rights'">
          <mat-icon class="text-base">verified_user</mat-icon>
          <span>Parking Rights ({{ parkingRights().length }})</span>
        </button>

        <button (click)="activeTab.set('transactions')"
          class="py-2.5 px-1 border-b-2 font-semibold text-xs sm:text-sm whitespace-nowrap transition-all flex items-center gap-1.5"
          [class.border-blue-600]="activeTab() === 'transactions'"
          [class.text-blue-600]="activeTab() === 'transactions'"
          [class.border-transparent]="activeTab() !== 'transactions'"
          [class.text-slate-500]="activeTab() !== 'transactions'"
          [class.hover:text-slate-700]="activeTab() !== 'transactions'">
          <mat-icon class="text-base">receipt_long</mat-icon>
          <span>Stays & Sessions ({{ transactions().length }})</span>
        </button>

        <button (click)="activeTab.set('organizations')"
          class="py-2.5 px-1 border-b-2 font-semibold text-xs sm:text-sm whitespace-nowrap transition-all flex items-center gap-1.5"
          [class.border-blue-600]="activeTab() === 'organizations'"
          [class.text-blue-600]="activeTab() === 'organizations'"
          [class.border-transparent]="activeTab() !== 'organizations'"
          [class.text-slate-500]="activeTab() !== 'organizations'"
          [class.hover:text-slate-700]="activeTab() !== 'organizations'">
          <mat-icon class="text-base">corporate_fare</mat-icon>
          <span>Organizations & Pools ({{ organizations().length }})</span>
        </button>

        <button (click)="activeTab.set('balance')"
          class="py-2.5 px-1 border-b-2 font-semibold text-xs sm:text-sm whitespace-nowrap transition-all flex items-center gap-1.5"
          [class.border-blue-600]="activeTab() === 'balance'"
          [class.text-blue-600]="activeTab() === 'balance'"
          [class.border-transparent]="activeTab() !== 'balance'"
          [class.text-slate-500]="activeTab() !== 'balance'"
          [class.hover:text-slate-700]="activeTab() !== 'balance'">
          <mat-icon class="text-base">account_balance_wallet</mat-icon>
          <span>Credit ({{ totalCreditLabel() }})</span>
        </button>
      </nav>
    </div>

    <!-- ── Loading Indicator ─────────────────────────────────────────────── -->
    @if (loadingEnriched()) {
    <div class="flex flex-col items-center justify-center py-12 text-slate-400 space-y-2">
      <mat-spinner diameter="32"></mat-spinner>
      <span class="text-xs">Loading enriched license plate details…</span>
    </div>
    } @else {

    <!-- ═══════════════════════════════════════════════════════════════════ -->
    <!-- TAB 1: PARKERS & OWNERSHIP RESOLUTION                               -->
    <!-- ═══════════════════════════════════════════════════════════════════ -->
    @if (activeTab() === 'parkers') {
    <div class="space-y-4">

      <!-- Status Banners -->
      @if (isSettled()) {
      <div class="bg-emerald-50 border border-emerald-200 rounded-lg p-3 flex items-start gap-2.5">
        <mat-icon class="text-emerald-600 text-lg mt-0.5">check_circle</mat-icon>
        <div>
          <h4 class="text-xs font-bold text-emerald-900">Ownership Settled</h4>
          <p class="text-[11px] text-emerald-800 mt-0.5">
            Exactly one registered account holds this plate. Billing and invoices have a single clear recipient.
          </p>
        </div>
      </div>
      } @else if (registeredHolders().length > 1) {
      <div class="bg-amber-50 border border-amber-200 rounded-lg p-3 flex items-start gap-2.5">
        <mat-icon class="text-amber-600 text-lg mt-0.5">warning</mat-icon>
        <div>
          <h4 class="text-xs font-bold text-amber-900">Multiple Account Dispute Detected</h4>
          <p class="text-[11px] text-amber-800 mt-0.5">
            {{ registeredHolders().length }} registered accounts claim or hold this plate. Invoicing has no single
            recipient until one owner is chosen below.
          </p>
        </div>
      </div>
      } @else if (guestHolders().length > 0 && registeredHolders().length === 0) {
      <div class="bg-blue-50 border border-blue-200 rounded-lg p-3 flex items-start gap-2.5">
        <mat-icon class="text-blue-600 text-lg mt-0.5">info</mat-icon>
        <div>
          <h4 class="text-xs font-bold text-blue-900">Guest / Unregistered Records</h4>
          <p class="text-[11px] text-blue-800 mt-0.5">
            This plate was recorded at gate detection without an account. You can assign it to a registered customer
            account below.
          </p>
        </div>
      </div>
      }

      <!-- Holders List -->
      <div class="space-y-2.5">
        <h3 class="text-xs font-bold uppercase tracking-wider text-slate-500">
          Current Holders ({{ holders().length }})
        </h3>

        @if (holders().length === 0) {
        <div class="p-3 bg-slate-50 border border-slate-200 rounded-lg text-xs text-slate-500">
          No parker records currently linked to this plate.
        </div>
        } @else {
        <div class="border border-slate-200 rounded-lg divide-y divide-slate-100 overflow-hidden">
          @for (holder of holders(); track holder.parkerId) {
          <div
            class="p-3 flex flex-col sm:flex-row sm:items-center justify-between gap-3 hover:bg-slate-50/50 transition-colors">
            <div class="space-y-1">
              <div class="flex items-center gap-2">
                <span class="text-[10px] px-2 py-0.5 rounded-full font-semibold"
                  [class]="holder.isRegistered ? 'bg-blue-50 text-blue-700 border border-blue-200' : 'bg-slate-100 text-slate-600 border border-slate-200'">
                  {{ holder.isRegistered ? 'Registered Account' : 'Guest Record' }}
                </span>
                <span class="text-xs sm:text-sm font-semibold text-slate-800">{{ describeHolder(holder) }}</span>
              </div>

              @if (!holder.isRegistered) {
              <p class="text-[11px] text-slate-500">
                Automatic record created at gate entry. Has no login or invoicing credentials.
              </p>
              }

              <!-- Site Classification per site. Only active classifications are shown. -->
              @if (activeClassificationsForHolder(holder.parkerId); as activeClassifications) {
              @if (activeClassifications.length) {
              <div class="flex flex-wrap items-center gap-1.5">
                @for (c of activeClassifications; track c.id) {
                <span class="text-[10px] px-2 py-0.5 rounded-full font-semibold border bg-violet-50 text-violet-700 border-violet-200"
                  [matTooltip]="c.source + ' · from ' + (c.validFrom | date:'mediumDate') + (c.validTo ? ' to ' + (c.validTo | date:'mediumDate') : '')">
                  {{ getSpaceName(c.parkingSpaceId) }} · {{ classificationLabelKey(c.classification) | translate }}
                </span>
                }
              </div>
              } @else if (!loadingClassifications()) {
              <p class="text-[11px] text-slate-400">No site classification.</p>
              }
              }

              @if (holder.otherPlates?.length) {
              <div class="text-[11px] font-mono"
                [class]="!holder.isRegistered && holder.otherPlates!.length >= 2 ? 'text-red-600 font-semibold' : 'text-slate-500'">
                <span>Also linked to: </span>
                <span>{{ holder.otherPlates!.join(', ') }}</span>
              </div>
              }

              @if (!holder.isRegistered && holder.otherPlates!.length >= 2) {
              <p class="text-[11px] text-red-600 font-medium">
                ⚠️ Warning: 3+ plates on a guest record is likely corrupted data. Confirm before assigning.
              </p>
              }
            </div>

            @if (holder.isRegistered) {
            <button mat-stroked-button (click)="chooseHolderAsOwner(holder)"
              [disabled]="selectedParker()?.id === holder.parkerId"
              class="text-xs font-medium self-start sm:self-auto shrink-0 h-7">
              {{ selectedParker()?.id === holder.parkerId ? '✓ Selected' : 'Choose as Owner' }}
            </button>
            }
          </div>
          }
        </div>
        }
      </div>

      <!-- Sole Owner Reassignment Box -->
      <div class="bg-slate-50 border border-slate-200 rounded-xl p-4 space-y-3">
        <div>
          <h3 class="text-xs sm:text-sm font-bold text-slate-800">Set Sole Plate Owner</h3>
          <p class="text-[11px] text-slate-500 mt-0.5">
            Choose a holder above, or search for a registered customer account by email to make them the sole invoicing
            owner.
          </p>
        </div>

        <!-- Search Parker by Email -->
        <div class="flex gap-2 max-w-xl">
          <input type="email"
            class="flex-1 bg-white border border-slate-200 rounded-lg px-3 py-1.5 text-xs sm:text-sm text-slate-800 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
            placeholder="Customer email (e.g. name@company.com)..." [ngModel]="parkerQuery()"
            (ngModelChange)="parkerQuery.set($event)" (keyup.enter)="searchParkers()" />
          <button mat-stroked-button (click)="searchParkers()"
            [disabled]="isSearchingParkers() || !parkerQuery().trim()" class="font-medium text-xs rounded-lg h-8">
            {{ isSearchingParkers() ? 'Searching…' : 'Find Account' }}
          </button>
        </div>

        @if (parkerSearched() && !isSearchingParkers() && parkerResults().length === 0) {
        <div class="text-xs text-slate-500 bg-white p-2.5 rounded-lg border border-slate-200">
          No registered accounts found matching that email.
        </div>
        }

        @if (parkerResults().length > 0) {
        <div class="border border-slate-200 bg-white rounded-lg divide-y divide-slate-100 max-w-xl overflow-hidden">
          @for (parker of parkerResults(); track parker.id) {
          <div class="p-2.5 flex items-center justify-between gap-2.5 text-xs">
            <span class="truncate font-medium text-slate-700">{{ describeParker(parker) }}</span>
            <button mat-stroked-button (click)="selectParker(parker)" [disabled]="selectedParker()?.id === parker.id"
              class="text-xs font-medium h-7">
              {{ selectedParker()?.id === parker.id ? '✓ Chosen' : 'Select' }}
            </button>
          </div>
          }
        </div>
        }

        <!-- Impact Preview & Apply Button -->
        @if (selectedParker(); as target) {
        <div class="bg-white border-2 border-blue-200 rounded-lg p-3 space-y-2.5">
          <div class="text-xs sm:text-sm font-semibold text-slate-900">
            Selected Owner: <span class="text-blue-600">{{ describeParker(target) }}</span>
          </div>

          <div class="text-xs text-slate-600 space-y-1 bg-slate-50 p-2.5 rounded-md border border-slate-100">
            <div class="font-semibold text-slate-700 mb-1">Execution Impact:</div>
            <ul class="list-disc pl-4 space-y-0.5 text-[11px]">
              @for (holder of registeredHolders(); track holder.parkerId) {
              @if (holder.parkerId !== target.id) {
              <li>Detaches <strong>{{ describeHolder(holder) }}</strong> from this plate.</li>
              }
              }
              @if (guestHolders().length) {
              <li>Folds in {{ guestHolders().length }} guest record(s), moving their stays and rights to this account.
              </li>
              }
              @if (guestCollateralPlates().length) {
              <li class="text-amber-700">Also associates collateral plates: <span class="font-mono font-semibold">{{
                  guestCollateralPlates().join(', ') }}</span>.</li>
              }
              <li>All site access & parking rights remain preserved.</li>
            </ul>
          </div>

          <button mat-flat-button color="primary" (click)="apply()" [disabled]="!canApply()"
            class="w-full sm:w-auto font-medium text-xs h-8">
            @if (isApplying()) {
            <div class="flex items-center gap-1.5">
              <mat-spinner diameter="14" class="border-white"></mat-spinner>
              <span>Applying Ownership…</span>
            </div>
            } @else {
            <span>Confirm & Make Sole Owner</span>
            }
          </button>
        </div>
        }

        <!-- Reassignment Result -->
        @if (lastResult(); as result) {
        <div class="bg-emerald-50 border border-emerald-200 rounded-lg p-3 text-xs text-emerald-900 space-y-1">
          <div class="font-bold flex items-center gap-1">
            <mat-icon class="text-sm text-emerald-600">check</mat-icon> Ownership Updated Successfully
          </div>
          @if (result.alreadyOwned) {
          <div>The account was already the registered owner.</div>
          }
          <div>Detached {{ result.detachedRegisteredOwners?.length ?? 0 }} other account(s).</div>
          <div>Absorbed {{ result.absorbedGuestOwnerIds?.length ?? 0 }} guest record(s).</div>
        </div>
        }
      </div>

    </div>
    }

    <!-- ═══════════════════════════════════════════════════════════════════ -->
    <!-- TAB 2: PARKING RIGHTS                                               -->
    <!-- ═══════════════════════════════════════════════════════════════════ -->
    @if (activeTab() === 'rights') {
    <div class="space-y-3">
      <div class="flex items-center justify-between text-xs text-slate-500">
        <span>Showing parking rights admitting this plate within your authorized spaces.</span>
        <span class="font-semibold text-slate-700">{{ parkingRights().length }} Right(s) found</span>
      </div>

      @for (poolId of capPoolIds(); track poolId) {
      <app-parker-pool-cap [poolParkingConfigId]="poolId" />
      }

      @if (parkingRights().length === 0) {
      <div class="py-10 bg-slate-50 border border-slate-200 rounded-xl text-center text-slate-400">
        <mat-icon class="text-3xl text-slate-300 block mb-1">verified_user</mat-icon>
        <p class="text-xs font-semibold text-slate-600">No Parking Rights Found</p>
        <p class="text-[11px] text-slate-400 mt-0.5">This plate currently has no active parking rights in your
          accessible spaces.</p>
      </div>
      } @else {
      <div class="grid grid-cols-1 xl:grid-cols-2 gap-3.5">
        @for (right of parkingRights(); track right.id) {
        @let validity = getRightValidity(right);
        @let sched = getRightSchedule(right);
        @let zones = getRightZones(right);

        <div
          class="bg-white border border-slate-200 rounded-xl p-4 space-y-3 hover:border-slate-300 transition-all shadow-xs flex flex-col justify-between">

          <!-- Right Card Header -->
          <div class="space-y-1.5">
            <div class="flex items-start justify-between gap-2.5">
              <div class="space-y-0.5">
                <h4 class="text-sm font-bold text-slate-900 tracking-tight">{{ right.name || 'Parking Right' }}</h4>
                <div class="text-xs text-slate-500 flex items-center gap-1.5">
                  <span class="font-medium text-slate-700">{{ right.parkingType?.type?.name || 'Standard Right'
                    }}</span>
                  @if (sched.typeLabel) {
                  <span>•</span>
                  <span class="text-[11px] text-slate-500">{{ sched.typeLabel }}</span>
                  }
                </div>
              </div>

              <!-- Status Badge -->
              <span class="text-[10px] px-2 py-0.5 rounded-full font-semibold shrink-0"
                [class]="right.isActive !== false ? 'bg-emerald-50 text-emerald-700 border border-emerald-200' : 'bg-slate-100 text-slate-500 border border-slate-200'">
                {{ right.isActive !== false ? 'Active' : 'Inactive' }}
              </span>
            </div>

            <!-- ── Multi-Section Details Grid ── -->
            <div class="grid grid-cols-1 sm:grid-cols-2 gap-2.5 pt-2 border-t border-slate-100 text-xs">

              <!-- 1. Schedule & Access Window -->
              <div class="bg-slate-50/80 rounded-lg p-2.5 border border-slate-100 space-y-1.5">
                <div class="text-[10px] font-bold uppercase tracking-wider text-slate-400 flex items-center gap-1">
                  <mat-icon class="text-xs text-slate-400">schedule</mat-icon> Access & Time Window
                </div>
                <div class="space-y-1 text-slate-700">
                  <!-- Validity -->
                  <div class="flex items-center gap-1.5">
                    <mat-icon class="text-xs text-blue-500">event</mat-icon>
                    <span class="font-medium">
                      @if (validity.isPermanent) {
                      <span class="text-emerald-700 font-semibold">Permanent (No expiry)</span>
                      } @else {
                      <span>{{ validity.from ? (validity.from | date:'shortDate') : 'Any' }} — {{ validity.to ?
                        (validity.to | date:'shortDate') : 'Open' }}</span>
                      }
                    </span>
                  </div>

                  <!-- Hours / Time Window -->
                  <div class="flex items-center gap-1.5">
                    <mat-icon class="text-xs text-indigo-500">access_time</mat-icon>
                    <span>Hours: <strong class="text-slate-800">{{ sched.timeWindow || '24/7' }}</strong></span>
                  </div>

                  <!-- Allowed Days -->
                  @if (sched.days) {
                  <div class="flex items-center gap-1.5">
                    <mat-icon class="text-xs text-slate-400">calendar_view_week</mat-icon>
                    <span class="text-[11px] text-slate-600">Days: {{ sched.days }}</span>
                  </div>
                  }
                </div>
              </div>

              <!-- 2. Permitted Spaces & Zones -->
              <div class="bg-slate-50/80 rounded-lg p-2.5 border border-slate-100 space-y-1.5">
                <div class="text-[10px] font-bold uppercase tracking-wider text-slate-400 flex items-center gap-1">
                  <mat-icon class="text-xs text-slate-400">place</mat-icon> Permitted Locations
                </div>

                <!-- Spaces (a zone-only right has none; "All Accessible Spaces" would misstate it) -->
                @if (right.allowedParkingSpaces?.length || zones.length === 0) {
                <div class="space-y-1">
                  <div class="text-[10px] text-slate-500 font-medium">Spaces:</div>
                  <div class="flex flex-wrap gap-1">
                    @if (!right.allowedParkingSpaces?.length) {
                    <span class="px-1.5 py-0.5 bg-slate-200/70 text-slate-700 rounded text-[10px]">All Accessible
                      Spaces</span>
                    } @else {
                    @for (sp of right.allowedParkingSpaces; track sp.id) {
                    <span
                      class="px-1.5 py-0.5 bg-blue-50 text-blue-700 border border-blue-200 rounded text-[10px] font-medium">
                      {{ getSpaceName(sp.parkingSpaceId) }}
                    </span>
                    }
                    }
                  </div>
                </div>
                }

                <!-- Zones -->
                @if (zones.length > 0) {
                <div class="space-y-1 pt-1">
                  <div class="text-[10px] text-slate-500 font-medium">Zones:</div>
                  <div class="flex flex-wrap gap-1">
                    @for (z of zones; track z) {
                    <span
                      class="px-1.5 py-0.5 bg-teal-50 text-teal-700 border border-teal-200 rounded text-[10px] font-medium">
                      {{ z }}
                    </span>
                    }
                  </div>
                </div>
                }
              </div>

            </div>

            <!-- Internal Comment / Notes -->
            @if (right.comment) {
            <div
              class="text-[11px] text-slate-600 bg-amber-50/70 border border-amber-200/70 p-2 rounded-lg italic flex items-start gap-1.5">
              <mat-icon class="text-xs text-amber-600 mt-0.5">comment</mat-icon>
              <span>"{{ right.comment }}"</span>
            </div>
            }
          </div>

          <!-- Card Metadata Footer -->
          <div class="pt-2 border-t border-slate-100 flex items-center justify-between text-[10px] text-slate-400">
            <span class="flex items-center gap-1 text-xs text-slate-700">
              <mat-icon class="text-sm text-slate-500">schedule</mat-icon>
              <span>Created</span>
              <span class="font-semibold">{{ right.createdAt ? (right.createdAt | date:'dd.MM.yyyy HH:mm') : '—' }}</span>
            </span>
            <span class="text-emerald-600 font-semibold flex items-center gap-1">
              <mat-icon class="text-xs">check_circle</mat-icon> Site Access Granted
            </span>
          </div>

        </div>
        }
      </div>
      }
    </div>
    }

    <!-- ═══════════════════════════════════════════════════════════════════ -->
    <!-- TAB 3: TRANSACTIONS & STAYS (SCOPED TO AUTHORIZED SPACES)           -->
    <!-- ═══════════════════════════════════════════════════════════════════ -->
    @if (activeTab() === 'transactions') {
    <div class="space-y-3">
      <div class="flex items-center justify-between text-xs text-slate-500">
        <span>Showing parking sessions and gate events recorded in your accessible spaces.</span>
        <span class="font-semibold text-slate-700">{{ transactions().length }} Session(s)</span>
      </div>

      @if (transactions().length === 0) {
      <div class="py-10 bg-slate-50 border border-slate-200 rounded-xl text-center text-slate-400">
        <mat-icon class="text-3xl text-slate-300 block mb-1">receipt_long</mat-icon>
        <p class="text-xs font-semibold text-slate-600">No Parking Sessions Found</p>
        <p class="text-[11px] text-slate-400 mt-0.5">No recorded parking stays in your authorized spaces for this plate.
        </p>
      </div>
      } @else {
      <div class="overflow-x-auto border border-slate-200 rounded-xl">
        <table class="w-full text-left text-xs">
          <thead class="bg-slate-50 text-slate-500 uppercase tracking-wider border-b border-slate-200">
            <tr>
              <th class="py-2.5 px-3 font-semibold">Parking Space & Zone</th>
              <th class="py-2.5 px-3 font-semibold">Entry (Started)</th>
              <th class="py-2.5 px-3 font-semibold">Exit (Ended)</th>
              <th class="py-2.5 px-3 font-semibold">Duration</th>
              <th class="py-2.5 px-3 font-semibold">Status</th>
            </tr>
          </thead>
          <tbody class="divide-y divide-slate-100 bg-white">
            @for (stay of transactions(); track stay.id) {
            <tr class="hover:bg-slate-50/80 transition-colors">
              <td class="py-2.5 px-3">
                <div class="font-semibold text-slate-800">{{ getSpaceName(stay.parkingSpaceId) }}</div>
                <div class="text-[11px] text-slate-400">
                  Zone: {{ stay.parkingZoneDetails?.name || 'All Zones' }}
                </div>
              </td>
              <td class="py-2.5 px-3 text-slate-700">
                {{ stay.startedAt ? (stay.startedAt | date:'medium') : '—' }}
              </td>
              <td class="py-2.5 px-3">
                @if (stay.endedAt) {
                <span class="text-slate-700">{{ stay.endedAt | date:'medium' }}</span>
                } @else {
                <span
                  class="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-emerald-50 text-emerald-700 border border-emerald-200">
                  <span class="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse"></span>
                  Active Stay
                </span>
                }
              </td>
              <td class="py-2.5 px-3 text-slate-600 font-mono">
                {{ formatDuration(stay.duration) || '—' }}
              </td>
              <td class="py-2.5 px-3">
                @if (stay.hasViolation) {
                <span
                  class="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold bg-red-50 text-red-700 border border-red-200">
                  Violation
                </span>
                } @else if (!stay.endedAt) {
                <span
                  class="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold bg-emerald-50 text-emerald-700 border border-emerald-200">
                  Parked
                </span>
                } @else {
                <span
                  class="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold bg-slate-100 text-slate-600 border border-slate-200">
                  Completed
                </span>
                }
              </td>
            </tr>
            }
          </tbody>
        </table>
      </div>
      }
    </div>
    }

    <!-- ═══════════════════════════════════════════════════════════════════ -->
    <!-- TAB 5: CREDIT                                                       -->
    <!-- ═══════════════════════════════════════════════════════════════════ -->
    @if (activeTab() === 'balance') {
    <app-license-plate-balance [wallets]="wallets()" [holders]="holders()" [spaces]="accessibleSpaces()"
      [canEdit]="canEditCredit()" (changed)="reloadWallets()"></app-license-plate-balance>
    }

    <!-- ═══════════════════════════════════════════════════════════════════ -->
    <!-- TAB 4: ORGANIZATIONS & POOLS                                        -->
    <!-- ═══════════════════════════════════════════════════════════════════ -->
    @if (activeTab() === 'organizations') {
    <div class="space-y-3">
      <div class="flex items-center justify-between text-xs text-slate-500">
        <span>Organizations and Pool Parking configurations associated with this license plate.</span>
        <span class="font-semibold text-slate-700">{{ organizations().length }} Organization(s)</span>
      </div>

      @if (organizations().length === 0) {
      <div class="py-10 bg-slate-50 border border-slate-200 rounded-xl text-center text-slate-400">
        <mat-icon class="text-3xl text-slate-300 block mb-1">corporate_fare</mat-icon>
        <p class="text-xs font-semibold text-slate-600">No Associated Organizations</p>
        <p class="text-[11px] text-slate-400 mt-0.5">This plate is not registered under any company pools or
          organization accounts.</p>
      </div>
      } @else {
      <div class="grid grid-cols-1 md:grid-cols-2 gap-3">
        @for (org of organizations(); track org.id) {
        <div
          class="bg-white border border-slate-200 rounded-xl p-3.5 space-y-2.5 hover:border-slate-300 transition-all shadow-xs">
          <div class="flex items-start justify-between gap-2.5">
            <div>
              <h4 class="text-xs sm:text-sm font-bold text-slate-900">{{ org.name }}</h4>
              @if (org.description) {
              <p class="text-[11px] text-slate-500 mt-0.5">{{ org.description }}</p>
              }
            </div>
            <span
              class="text-[10px] px-2 py-0.5 rounded-full font-semibold bg-purple-50 text-purple-700 border border-purple-200">
              Organization
            </span>
          </div>

          <!-- Pool configs admitting this plate -->
          @if (org.poolParkConfigs?.length) {
          <div class="space-y-1 text-xs">
            <div class="text-slate-500 font-semibold uppercase tracking-wider text-[10px]">Active Pool Configs:</div>
            <div class="flex flex-wrap gap-1">
              @for (pool of org.poolParkConfigs; track pool.id) {
              <span class="px-1.5 py-0.5 bg-slate-100 text-slate-700 rounded text-[10px]">
                {{ pool.name || 'Pool Parking' }} (Limit: {{ pool.maxCarsAllowed || '—' }})
              </span>
              }
            </div>
          </div>
          }

          <div class="pt-2 border-t border-slate-100 flex items-center justify-between text-[10px] text-slate-400">
            <span>Contact: {{ org.contactPerson || org.contactPhone || '—' }}</span>
            <span>Parkers: {{ org.parkers?.length ?? 0 }}</span>
          </div>
        </div>
        }
      </div>
      }
    </div>
    }

    }
  </div>
  }

  <!-- ── Edit Plate Modal ───────────────────────────────────────────────────── -->
  @if (showEditModal() && selectedPlate()) {
  <div class="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-xs">
    <div
      class="bg-white rounded-xl shadow-xl border border-slate-200 w-full max-w-md max-h-[90vh] flex flex-col overflow-hidden animate-in fade-in zoom-in duration-150">

      <!-- Modal Header -->
      <div class="p-4 border-b border-slate-100 flex items-center justify-between shrink-0">
        <div class="flex items-center gap-2">
          <mat-icon class="text-blue-600 text-lg">edit</mat-icon>
          <h3 class="text-sm font-bold text-slate-900">Edit License Plate</h3>
        </div>
        <button (click)="showEditModal.set(false)" class="p-1 text-slate-400 hover:text-slate-600 rounded-md">
          <mat-icon class="text-base">close</mat-icon>
        </button>
      </div>

      <!-- Modal Form -->
      <form [formGroup]="editForm" (ngSubmit)="savePlateEdit()" class="p-4 space-y-3 overflow-y-auto flex-1 text-xs">

        <!-- Plate Graphic Preview -->
        <div class="flex justify-center py-2 bg-slate-50 rounded-lg border border-slate-100">
          <app-plate [number]="editForm.get('fullNumber')?.value || 'SAMPLE'"
            [countryCode]="editForm.get('countryCode')?.value || 'AT'"></app-plate>
        </div>

        <!-- Full Number Field -->
        <div class="space-y-1">
          <label class="block text-xs font-semibold text-slate-700">License Plate Number</label>
          <input type="text" formControlName="fullNumber"
            class="w-full px-3 py-1.5 bg-white border border-slate-200 rounded-lg text-xs sm:text-sm font-mono uppercase focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
            placeholder="e.g. W1234AB" />
        </div>

        <!-- Country Code Field -->
        <div class="space-y-1">
          <label class="block text-xs font-semibold text-slate-700">Country Code</label>
          <select formControlName="countryCode"
            class="w-full px-3 py-1.5 bg-white border border-slate-200 rounded-lg text-xs focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500">
            @for (c of countries; track c.code) {
            <option [value]="c.code">{{ c.code }} (EU: {{ c.euBand }})</option>
            }
          </select>
        </div>

        <!-- Plate Type Field -->
        <div class="space-y-1">
          <label class="block text-xs font-semibold text-slate-700">Vehicle / Plate Type</label>
          <select formControlName="type"
            class="w-full px-3 py-1.5 bg-white border border-slate-200 rounded-lg text-xs focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500">
            @for (t of plateTypes; track t.value) {
            <option [value]="t.value">{{ t.label }}</option>
            }
          </select>
        </div>

        <!-- Actions -->
        <div class="pt-3 border-t border-slate-100 flex items-center justify-end gap-2">
          <button type="button" mat-stroked-button (click)="showEditModal.set(false)" class="text-xs h-8">
            Cancel
          </button>
          <button type="submit" mat-flat-button color="primary" [disabled]="editForm.invalid || isUpdatingPlate()"
            class="text-xs font-medium h-8">
            @if (isUpdatingPlate()) {
            <div class="flex items-center gap-1.5">
              <mat-spinner diameter="14" class="border-white"></mat-spinner>
              <span>Saving…</span>
            </div>
            } @else {
            <span>Save Changes</span>
            }
          </button>
        </div>
      </form>

    </div>
  </div>
  }

</div>